import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, Building2, ShoppingBag, History, Users, 
  Send, MessageSquare, Clock, ExternalLink, Plus, RefreshCw, UserCircle, X, FileUp, Upload, Wrench,
  Edit2, Trash2, Download, FileText, FolderOpen, Calendar, DollarSign, MapPin 
} from 'lucide-react';
import { API_BASE_URL } from '../config';

import CSActivityModal from './CSActivityModal';
import PermissionGate from './PermissionGate'; 
import { crmDataService } from '../services/crmDataService'; 

export default function CompanyList({ globalRefreshKey = 0, onNavigate, currentUser }: any) { 
  const [orders, setOrders] = useState<any[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [contactsMaster, setContactsMaster] = useState<any[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<number | string | null>(null);
  const [selectedClientName, setSelectedClientName] = useState(''); 
  const [clientDetail, setClientDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [quickMemo, setQuickMemo] = useState("");

  const [editingHistoryId, setEditingHistoryId] = useState<number | null>(null); 
  const [editHistoryContent, setEditHistoryContent] = useState(""); 

  const [isCSMode, setIsCSMode] = useState(false); 
  const [showCSModal, setShowCSModal] = useState(false); 

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [clientFiles, setClientFiles] = useState<any[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showFileCabinet, setShowFileCabinet] = useState(false); 

  const [showReportModal, setShowReportModal] = useState(false);
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0]); 
  const [budget, setBudget] = useState("");
  const [selectedRegion, setSelectedRegion] = useState(""); 
  const [selectedContactPerson, setSelectedContactPerson] = useState(""); 
  const [autoPhone, setAutoPhone] = useState(""); 
  const [clientSearchInput, setClientSearchInput] = useState(""); 
  const [showDropdown, setShowDropdown] = useState(false); 

  const [showOrderModal, setShowOrderModal] = useState(false);
  const [newOrder, setNewOrder] = useState({
    order_no: '',
    order_date: new Date().toISOString().split('T')[0], 
    client_name: '',
    manager_name: '',
    note: '',
    win_price: 0 
  });

  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editClientData, setEditClientData] = useState({ id: 0, client_name: '', region: '' });

  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [editOrderData, setEditOrderData] = useState<any>({ id: 0, order_no: '', order_date: '', manager_name: '', note: '', win_price: 0 });

  const handleConfirmUpload = async () => {
    if (!uploadFile) {
      alert('엑셀 파일을 선택해주세요.');
      return;
    }

    const lower = uploadFile.name.toLowerCase();
    if (!(lower.endsWith('.xlsx') || lower.endsWith('.xls'))) {
      alert('엑셀 파일(.xlsx, .xls)만 업로드할 수 있습니다.');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('target', 'orders');

    setIsUploading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/files/upload-excel`, {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        alert("주문서 엑셀 업로드가 완료되었습니다.\n서버 재시작 시 자동으로 반영됩니다.");
        setIsUploadModalOpen(false);
        setUploadFile(null);
        fetchAllData(); 
      } else {
        const errData = await res.json();
        alert(`업로드 실패: ${errData.detail || '오류 발생'}`);
      }
    } catch (error) {
      alert("서버 연결 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!window.confirm(`[${file.name}] 파일을 거래처 '${selectedClientName}'에 업로드하시겠습니까?`)) {
      return;
    }

    try {
      setDetailLoading(true);
      const res = await crmDataService.uploadClientFile(
        selectedClientName, 
        file, 
        currentUser?.name || '관리자'
      );
      
      if (res.status === 'success') {
        alert("파일 업로드가 완료되었습니다.");
        handleClientClick(selectedClientId!, selectedClientName); 
      } else {
        alert(`파일 업로드 실패: ${res.message || '알 수 없는 오류'}`);
      }
    } catch (error) { 
      console.error(error); 
    } finally { 
      setDetailLoading(false); 
    }
  };

  const handleDeleteFile = async (filePath: string, fileName: string) => {
    if (!window.confirm(`[${fileName}] 파일을 정말 삭제하시겠습니까?\n(삭제 후 복구할 수 없습니다)`)) {
      return;
    }

    try {
      setDetailLoading(true);
      const res = await crmDataService.deleteClientFile(filePath);
      
      if (res.status === 'success') {
        alert("파일이 삭제되었습니다.");
        handleClientClick(selectedClientId!, selectedClientName); 
      } else {
        alert(`파일 삭제 실패: ${res.message || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [ordRes, dealRes, contRes] = await Promise.all([
        fetch(`${API_BASE_URL}/orders`).catch(() => ({ json: () => [] })),
        fetch(`${API_BASE_URL}/forecast/list`).catch(() => ({ json: () => [] })),
        fetch(`${API_BASE_URL}/contacts`).catch(() => ({ json: () => [] }))
      ]);
      const ordData = await ordRes.json();
      const dealData = await dealRes.json();
      const contData = await contRes.json();

      setOrders(Array.isArray(ordData) ? ordData : []);
      setDeals(Array.isArray(dealData) ? dealData : []);
      setContactsMaster(Array.isArray(contData) ? contData : []);
    } catch (error) { 
      console.error(error); 
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { fetchAllData(); }, [globalRefreshKey]);

  const uniqueClients = useMemo(() => {
    const clientMap = new Map();
    
    orders.forEach(order => {
      const name = (order.client_name || order.거래처명 || order.거래처)?.trim();
      if (!name) return;
      
      let r = (order.region || order.지역 || '').trim();
      if (r === '-') r = ''; 

      if (!clientMap.has(name)) {
        clientMap.set(name, { id: order.client_id, client_name: name, order_count: 0, region: r, has_deal: false });
      }
      clientMap.get(name).order_count += 1;
      if (r) clientMap.get(name).region = r; 
    });

    deals.forEach(d => {
      const name = (d.company_name || d.거래처)?.trim();
      if (!name) return;
      
      let r = (d.region || d.지역 || '').trim();
      if (r === '-') r = '';

      if (!clientMap.has(name)) {
        clientMap.set(name, { id: `deal_${d.id}`, client_name: name, order_count: 0, region: r, has_deal: true });
      } else {
        clientMap.get(name).has_deal = true; 
        if (r) clientMap.get(name).region = r; 
      }
    });

    contactsMaster.forEach(c => {
      const name = (c.company || c.company_name || c.거래처)?.trim();
      if (!name) return;
      
      let r = (c.region || c.지역 || '').trim();
      if (r === '-') r = '';

      if (!clientMap.has(name)) {
        clientMap.set(name, { id: `cont_${c.id}`, client_name: name, order_count: 0, region: r, has_deal: false });
      } else {
        if (!clientMap.get(name).region && r) {
          clientMap.set(name, { id: clientMap.get(name).id, client_name: name, order_count: clientMap.get(name).order_count, region: r, has_deal: clientMap.get(name).has_deal });
        }
      }
    });

    return Array.from(clientMap.values()).filter(c => 
      c.client_name.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => a.client_name.localeCompare(b.client_name)); 
  }, [orders, deals, contactsMaster, searchTerm]);

  const filteredModalClients = useMemo(() => {
    return uniqueClients.filter(c => {
      const matchesRegion = !selectedRegion || c.region === selectedRegion;
      const matchesInput = c.client_name.toLowerCase().includes(clientSearchInput.toLowerCase());
      return matchesRegion && matchesInput;
    });
  }, [uniqueClients, selectedRegion, clientSearchInput]);

  const availableRegions = useMemo(() => {
    const regions = uniqueClients.map(c => c.region).filter(Boolean);
    return Array.from(new Set(regions)).sort();
  }, [uniqueClients]);


  const handleClientClick = async (clientId: number | string, clientName: string) => {
    setSelectedClientId(clientId);
    setSelectedClientName(clientName);
    setClientSearchInput(clientName); 
    setDetailLoading(true);
    
    try {
      const cleanId = typeof clientId === 'number' ? clientId : 0;
      
      const [detailRes, filesData] = await Promise.all([
        fetch(`${API_BASE_URL}/clients/history/search?id=${cleanId}&name=${encodeURIComponent(clientName)}`),
        crmDataService.getClientFiles(clientName)
      ]);
      
      let detailData = { orders: [], deal_history: [] };
      if (detailRes.ok) {
        detailData = await detailRes.json();
      }

      setClientFiles(Array.isArray(filesData) ? filesData : []);

      const matchedOrders = orders.filter(ord => {
        const oName = (ord.client_name || ord.거래처명 || ord.거래처 || '').trim();
        return oName === clientName.trim();
      });

      const cleanHistory = (detailData.deal_history || [])
        .filter((v: any) => v.content) 
        .filter((v: any, i: number, a: any[]) => 
          a.findIndex((t: any) => 
            t.content === v.content && 
            t.created_at === v.created_at && 
            t.editor === v.editor
          ) === i
        )
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      const contactRes = await fetch(`${API_BASE_URL}/contacts`);
      const allContacts = await contactRes.json();
      const matchedContacts = allContacts.filter((c: any) => {
        const company = (c.company || c.company_name || '').trim();
        return clientName.includes(company) || company.includes(clientName);
      });

      setClientDetail({ 
        orders: matchedOrders, 
        deal_history: cleanHistory, 
        contacts: matchedContacts 
      });

      const foundClientInMaster = uniqueClients.find(c => c.client_name === clientName);
      if (foundClientInMaster) {
        setSelectedRegion(foundClientInMaster.region || "");
      }
      if (matchedContacts.length > 0) {
        const firstContact = matchedContacts[0];
        setSelectedContactPerson(firstContact.contactPerson || firstContact.name || "");
        setAutoPhone(firstContact.phone || "");
      } else {
        setSelectedContactPerson("");
        setAutoPhone("");
      }

    } catch (error) { 
      console.error("상세 로드 실패:", error); 
    } finally { 
      setDetailLoading(false); 
    }
  };

  const [autoClickTarget, setAutoClickTarget] = useState<string | null>(null);

  useEffect(() => {
    const targetClient = sessionStorage.getItem('targetClientSearch');
    if (targetClient) {
      setSearchTerm(targetClient); 
      setAutoClickTarget(targetClient); 
      sessionStorage.removeItem('targetClientSearch'); 
    }
  }, []); 

  useEffect(() => {
    if (autoClickTarget && uniqueClients.length > 0) {
      const matchedClient = uniqueClients.find(c => c.client_name === autoClickTarget);
      if (matchedClient) {
        handleClientClick(matchedClient.id, matchedClient.client_name); 
        setAutoClickTarget(null); 
      }
    }
  }, [uniqueClients, autoClickTarget]);

  const handleSaveOrder = async () => {
    if (!newOrder.client_name.trim()) {
      alert("거래처명을 입력해주세요.");
      return;
    }
    if (!newOrder.order_no.trim()) {
      alert("주문번호를 입력해주세요.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newOrder)
      });
      if (res.ok) {
        alert("주문서가 등록되었습니다.");
        setShowOrderModal(false);
        
        const newOrderForUI = { ...newOrder, id: Date.now() }; 
        if (selectedClientName === newOrder.client_name) {
          setClientDetail((prev: any) => ({
            ...prev,
            orders: [newOrderForUI, ...(prev?.orders || [])]
          }));
        }
        setOrders(prev => [newOrderForUI, ...prev]);

        setNewOrder({ order_no: '', order_date: new Date().toISOString().split('T')[0], client_name: '', manager_name: '', note: '', win_price: 0 });
        
        fetchAllData(); 
      }
    } catch (error) { console.error(error); }
  };

  // 🚨 [버그 픽스 1] 저장 시 선택된 담당자와 연락처 데이터(payload) 누락 픽스!
  const handleSaveReportModal = async () => {
    if (!quickMemo.trim() || !selectedClientName) {
      alert("상담 세부 내용을 입력해주세요.");
      return;
    }
    if (!selectedContactPerson) {
      alert("등록된 담당자가 없는 거래처입니다.\n고객 관리 탭에서 담당자 정보를 사전에 생성해두어야 정식 보고서 제출이 가능합니다.");
      return;
    }

    const storedData = localStorage.getItem('ieg_user_profile');
    let realName = "관리자";

    if (storedData) {
      try {
        const userData = JSON.parse(storedData);
        realName = userData.name || userData.user?.name || "관리자";
      } catch (e) {
        console.error("로그인 정보 파싱 실패:", e);
      }
    }

    const finalEditorName = isCSMode ? `[CS팀] ${realName}` : realName;

    // 🚨 픽스: 연락처와 전화번호를 백엔드 저장 바구니에 확실하게 담습니다.
    const payload = {
      deal_id: selectedClientName,
      content: quickMemo,
      activity_type: isCSMode ? "CS상담" : "영업상담",
      last_editor: finalEditorName,
      visit_date: visitDate, 
      budget: budget.trim(),
      contact_person: selectedContactPerson, // 🚨 [추가] 선택된 담당자
      phone: autoPhone                       // 🚨 [추가] 선택된 번호
    };

    try {
      setDetailLoading(true);
      const res = await crmDataService.saveDealActivity(payload);

      if (res && res.status !== "error") {
        alert("종합 영업 활동 보고서가 성공적으로 누적 적재되었습니다.");
        setQuickMemo("");
        setBudget("");
        setIsCSMode(false);
        setShowReportModal(false); 
        handleClientClick(selectedClientId!, selectedClientName); 
      } else {
        alert("저장에 실패했습니다.");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleClientSelectInModal = (clientName: string) => {
    setSelectedClientName(clientName);
    setClientSearchInput(clientName);
    setShowDropdown(false);

    const found = uniqueClients.find(c => c.client_name === clientName);
    if (found) {
      setSelectedRegion(found.region || "");
    }

    const matchedContacts = contactsMaster.filter((c: any) => {
      const company = (c.company || c.company_name || '').trim();
      return clientName.includes(company) || company.includes(clientName);
    });

    if (matchedContacts.length > 0) {
      const firstContact = matchedContacts[0];
      setSelectedContactPerson(firstContact.contactPerson || firstContact.name || "");
      setAutoPhone(firstContact.phone || "");
    } else {
      setSelectedContactPerson("");
      setAutoPhone("");
    }
  };

  const handleRegionSelectInModal = (regionName: string) => {
    setSelectedRegion(regionName);
    setSelectedClientName("");
    setClientSearchInput(""); 
    setSelectedContactPerson("");
    setAutoPhone("");
  };

  // 🚨 [버그 픽스 2] 드롭다운 변경 시에도 깐깐한 일치(===)가 아닌 느슨한 포함(includes)으로 담당자를 찾도록 수정!
  const handleContactPersonSelectInModal = (personName: string) => {
    setSelectedContactPerson(personName);
    
    // 🚨 픽스: 선택된 업체명으로 느슨하게(포함) 찾습니다.
    const matchedContacts = contactsMaster.filter((c: any) => {
      const company = (c.company || c.company_name || '').trim();
      return company && (selectedClientName.includes(company) || company.includes(selectedClientName));
    });

    const foundContact = matchedContacts.find(c => (c.contactPerson || c.name || '') === personName);
    
    if (foundContact) {
      setAutoPhone(foundContact.phone || "");
    } else {
      setAutoPhone("");
    }
  };

  const handleUpdateHistory = async (id: any) => {
    if (id === undefined || id === null) {
      alert("이 메모는 고유 ID가 없어서 수정할 수 없습니다.");
      return;
    }

    if (!editHistoryContent.trim()) return;

    const storedData = localStorage.getItem('ieg_user_profile');
    let realName = "관리자";
    if (storedData) {
      try {
        const userData = JSON.parse(storedData);
        realName = userData.name || userData.user?.name || "관리자";
      } catch (e) { console.error(e); }
    }

    try {
      const payload = {
        history_id: Number(id), 
        new_content: editHistoryContent,
        editor: realName
      };

      const res = await fetch(`${API_BASE_URL}/activity/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setEditingHistoryId(null); 
        handleClientClick(selectedClientId!, selectedClientName); 
      } else {
        const errData = await res.json();
        alert(`수정 실패: ${JSON.stringify(errData.detail || errData)}`);
      }
    } catch (error) {
      console.error("수정 오류:", error);
    }
  };

  const handleUpdateClient = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/clients/${editClientData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editClientData)
      });
      if (res.ok) {
        alert("수정되었습니다.");
        setShowEditClientModal(false);
        
        if (selectedClientId === editClientData.id) {
          setSelectedClientName(editClientData.client_name);
        }
        setOrders(prev => prev.map(o => o.client_id === editClientData.id ? { ...o, client_name: editClientData.client_name, region: editClientData.region } : o));
        
        fetchAllData();
      }
    } catch (error) { console.error(error); }
  };

  const handleDeleteClient = async (id: number) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/clients/${id}`, { method: 'DELETE' });
      if (res.ok) { fetchAllData(); }
    } catch (error) { console.error(error); }
  };

  const handleUpdateOrder = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/orders/${editOrderData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editOrderData)
      });
      if (res.ok) {
        setShowEditOrderModal(false);
        
        setClientDetail((prev: any) => ({
          ...prev,
          orders: prev.orders.map((o: any) => o.id === editOrderData.id ? { ...o, ...editOrderData } : o)
        }));
        setOrders(prev => prev.map(o => o.id === editOrderData.id ? { ...o, ...editOrderData } : o));

        fetchAllData();
      }
    } catch (error) { console.error(error); }
  };

  const handleDeleteOrder = async (id: number) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/orders/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setClientDetail((prev: any) => ({
          ...prev,
          orders: prev.orders.filter((o: any) => o.id !== id)
        }));
        setOrders(prev => prev.filter(o => o.id !== id));

        fetchAllData();
      }
    } catch (error) { console.error(error); }
  };

  return (
    <div className="relative flex flex-col h-[calc(100vh-2rem)] bg-[#f1f5f9] p-8 gap-8 font-sans overflow-hidden">
      
      {/* 🔝 상단 영역: 마스터 리스트 (입체감 부여) */}
      <div className="h-[40%] bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-200/60 overflow-hidden flex flex-col shrink-0">
        <div className="px-8 py-5 border-b border-slate-100 flex justify-between items-center bg-white">
          <div className="flex items-center gap-4">
            <div className="p-2.5 bg-slate-900 rounded-xl text-white"><Building2 size={20} /></div>
            <div>
              <h2 className="font-black text-slate-900 text-lg tracking-tight">거래처 관리</h2>
              <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">Master Client Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
             <button 
                onClick={() => onNavigate?.('contacts')} 
                className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-xl text-xs font-black transition-all border border-indigo-100 shadow-sm active:scale-95"
             >
                <UserCircle size={16} /> 전체 고객 관리로 이동
             </button>

             <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input type="text" placeholder="학교명 또는 담당자 검색..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="pl-11 pr-5 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold w-64 outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all" />
             </div>

             <PermissionGate user={currentUser} action="contacts_edit">
               <button
                 onClick={() => {
                   setUploadFile(null); 
                   setIsUploadModalOpen(true);
                 }}
                 className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-xl text-xs font-black transition-all border border-emerald-100 shadow-sm active:scale-95"
               >
                 <Upload size={14} /> 주문서 엑셀 업로드
               </button>
             </PermissionGate>

             <button onClick={fetchAllData} className="p-2.5 hover:bg-slate-50 rounded-xl border border-slate-200 transition-all active:scale-95">
                <RefreshCw size={18} className={loading ? "animate-spin text-blue-600" : "text-slate-400"} />
             </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto custom-scrollbar px-6 pb-4">
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-slate-400 text-[10px] font-black uppercase tracking-widest">
                <th className="py-4 pl-4 w-[60px]">No.</th>
                <th className="py-4 px-4">거래처명</th>
                <th className="py-4 px-4 text-center">지역 (통합)</th>
                <th className="py-4 px-4 text-center">진행중 영업</th>
                <th className="py-4 px-4 text-center">누적주문</th>
                <th className="py-4 px-4 text-center w-[80px]">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {uniqueClients.map((client, idx) => (
                <tr key={client.id} onClick={() => handleClientClick(client.id, client.client_name)}
                  className={`group cursor-pointer transition-all duration-200 ${selectedClientId === client.id ? 'bg-blue-50/50' : 'hover:bg-slate-50/80'}`}>
                  <td className="py-4 pl-4 font-mono text-[11px] text-slate-400">{idx + 1}</td>
                  <td className={`py-4 px-4 font-black text-[14px] ${selectedClientId === client.id ? 'text-blue-600' : 'text-slate-700'}`}>{client.client_name}</td>
                  <td className="py-4 px-4 text-center text-slate-500 font-bold text-[11px]">{client.region || '-'}</td>
                  <td className="py-4 px-4 text-center">
                    {client.has_deal ? (
                      <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.6)]"></span>
                    ) : <span className="text-slate-300">-</span>}
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-[11px] font-black ${selectedClientId === client.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {client.order_count}건
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    {typeof client.id === 'number' ? (
                      <PermissionGate user={currentUser} action="contacts_edit">
                        <div className="flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={(e) => { e.stopPropagation(); setEditClientData({ id: client.id, client_name: client.client_name, region: client.region || '' }); setShowEditClientModal(true); }}
                            className="p-1.5 text-slate-400 hover:text-blue-600 bg-white rounded-md shadow-sm border border-slate-100" title="수정">
                            <Edit2 size={12}/>
                          </button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleDeleteClient(client.id); }}
                            className="p-1.5 text-slate-400 hover:text-rose-600 bg-white rounded-md shadow-sm border border-slate-100" title="삭제">
                            <Trash2 size={12}/>
                          </button>
                        </div>
                      </PermissionGate>
                    ) : <span className="text-[10px] text-slate-300">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ⬇️ 하단: 3단 일체형 정보 패널 */}
      <div className="flex-1 flex gap-8 min-h-0">
        {!selectedClientId ? (
          <div className="flex-1 bg-white/40 rounded-[40px] border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400">
             <Building2 size={80} className="mb-6 opacity-10" />
             <p className="font-black text-xl tracking-tight opacity-40">상단 리스트에서 분석할 거래처를 선택하세요</p>
          </div>
        ) : (
          <>
            {/* 1. 주문 내역 */}
            <div className="flex-[0.9] bg-white rounded-[32px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-200/60 flex flex-col overflow-hidden">
               <div className="px-7 py-6 border-b border-slate-50 flex items-center justify-between bg-white shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-rose-50 text-rose-500 rounded-xl"><ShoppingBag size={18} /></div>
                    <h3 className="font-black text-slate-800 text-[15px]">주문서 내역</h3>
                  </div>
                  <PermissionGate user={currentUser} action="contacts_edit">
                    <button 
                      onClick={() => {
                        setNewOrder(prev => ({ ...prev, client_name: selectedClientName }));
                        setShowOrderModal(true);
                      }}
                      className="p-2 bg-rose-500 text-white hover:bg-rose-600 rounded-xl transition-all shadow-lg active:scale-95"
                    >
                      <Plus size={16} strokeWidth={3} />
                    </button>
                  </PermissionGate>
               </div>
               <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-50/20">
                  <div className="space-y-4">
                    {clientDetail?.orders?.map((ord: any, i: number) => (
                      <div key={i} className="p-5 bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all group relative">
                         <PermissionGate user={currentUser} action="contacts_edit">
                           <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                             <button onClick={() => { setEditOrderData({ ...ord }); setShowEditOrderModal(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 bg-slate-50 rounded-md" title="수정"><Edit2 size={12}/></button>
                             <button onClick={() => handleDeleteOrder(ord.id)} className="p-1.5 text-slate-400 hover:text-rose-600 bg-slate-50 rounded-md" title="삭제"><Trash2 size={12}/></button>
                           </div>
                         </PermissionGate>

                         <div className="flex justify-between items-center mb-2 pr-12">
                            <span className="text-[10px] font-mono text-slate-400 font-black">{ord.order_date}</span>
                            <div className="text-[10px] font-black text-slate-300 group-hover:text-blue-500 transition-colors">{ord.order_no}</div>
                         </div>
                         <div className="mb-3 mt-1">
                           {ord.note ? (
                             <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-50 text-rose-700 text-[11px] font-black border border-rose-100 leading-tight">
                               📍 {ord.note}
                             </span>
                           ) : (
                             <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-50 text-slate-400 text-[11px] font-bold border border-slate-100">
                               본사 일괄 납품
                             </span>
                           )}
                         </div>
                         <div className="pt-3 border-t border-slate-50 flex justify-end">
                           <span className="text-[14px] font-black text-slate-900 font-mono">
                             ₩{(ord.win_price || 0).toLocaleString()}
                           </span>
                         </div>
                      </div>
                    ))}
                  </div>
               </div>
            </div>

            {/* 2. 영업 히스토리 & 파일함 */}
            <div className="flex-[1.3] flex gap-4 min-w-0 transition-all duration-500 ease-in-out">
               <div 
                 className={`flex-1 bg-white rounded-[32px] shadow-sm border-2 flex flex-col overflow-hidden transition-all duration-300 ${isDragging ? 'border-blue-500 ring-8 ring-blue-50' : 'border-blue-500/20'}`}
                 onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                 onDragLeave={() => setIsDragging(false)}
                 onDrop={handleFileDrop}
               >
                 <div className="px-7 py-5 border-b border-slate-100 bg-slate-900 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-3 text-white">
                      <History size={20} className="text-blue-400" />
                      <h3 className="font-black text-[15px]">{selectedClientName} 타임라인</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <PermissionGate user={currentUser} action="cs_edit">
                        <button onClick={() => setShowCSModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-black rounded-lg transition-colors shadow-sm active:scale-95">
                          <Wrench size={14} /> CS 정식 활동 추가
                        </button>
                      </PermissionGate>
                      <span className="text-[10px] font-black text-blue-300 bg-blue-500/20 px-2 py-1 rounded-full border border-blue-500/30">LIVE</span>
                      <button 
                        onClick={() => setShowFileCabinet(!showFileCabinet)}
                        className={`p-2 rounded-lg transition-all ml-1 ${showFileCabinet ? 'bg-blue-500 text-white shadow-lg shadow-blue-200' : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'}`}
                        title="파일 보관함 열기"
                      >
                        <FolderOpen size={16} />
                      </button>
                    </div>
                 </div>

                  <div className="flex-1 overflow-y-auto p-6 bg-white custom-scrollbar">
                    {clientDetail?.deal_history?.length > 0 ? (
                      <div className="border-l-2 border-slate-100 ml-2 pl-6 space-y-6">
                        {clientDetail.deal_history.map((hist: any, i: number) => {
                          const isCS = hist.editor?.includes('[CS팀]');
                          const isEditing = editingHistoryId === hist.id; 

                          return (
                            <div key={i} className="relative group">
                              <div className={`absolute -left-[31px] top-1 w-3 h-3 bg-white border-2 rounded-full z-10 ${isCS ? 'border-purple-600' : 'border-blue-600'}`} />
                              
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${isCS ? 'text-white bg-purple-600 shadow-sm' : 'text-blue-600 bg-blue-50'}`}>
                                    {hist.editor}
                                  </span>
                                  <span className="text-[10px] font-bold text-slate-400">{hist.created_at?.substring(2, 16)}</span>
                                </div>
                                
                                {!isEditing && (
                                  <PermissionGate user={currentUser} action="contacts_edit">
                                    <button 
                                      onClick={() => { setEditingHistoryId(hist.id); setEditHistoryContent(hist.content); }}
                                      className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-blue-600 transition-all"
                                      title="내용 수정"
                                    >
                                      <Edit2 size={12} />
                                    </button>
                                  </PermissionGate>
                                )}
                              </div>

                              {isEditing ? (
                                <div className="space-y-2 animate-in fade-in duration-200">
                                  <textarea 
                                    value={editHistoryContent}
                                    onChange={(e) => setEditHistoryContent(e.target.value)}
                                    className="w-full p-3 rounded-2xl border-2 border-blue-400 text-[12px] font-bold outline-none shadow-inner"
                                    rows={3}
                                  />
                                  <div className="flex justify-end gap-1">
                                    <button onClick={() => setEditingHistoryId(null)} className="px-3 py-1 text-[10px] font-black bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200">취소</button>
                                    <button onClick={() => handleUpdateHistory(hist.id)} className="px-3 py-1 text-[10px] font-black bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-md">저장하기</button>
                                  </div>
                                </div>
                              ) : (
                                <div className={`p-3.5 rounded-2xl border text-[12px] font-bold text-slate-700 leading-relaxed transition-all ${isCS ? 'bg-purple-50/30 border-purple-100 group-hover:bg-purple-50' : 'bg-slate-50 border-slate-100 group-hover:bg-blue-50/30'}`}>
                                  
                                  <div className="flex flex-wrap items-center gap-1.5 mb-2 pb-2 border-b border-dashed border-slate-200 text-[10px] text-slate-400 font-bold font-mono">
                                    <span className="flex items-center gap-1 text-slate-500">📅 방문일: {hist.visit_date || (hist.created_at ? String(hist.created_at).substring(0, 10) : "-")}</span>
                                    
                                    {hist.budget && String(hist.budget).trim() !== "" && String(hist.budget).trim() !== "-" && (
                                      <span className="flex items-center gap-0.5 text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 shadow-sm">💰 예산: ₩{hist.budget}</span>
                                    )}

                                    {hist.contact_person && hist.contact_person !== "-" && (
                                      <span className="flex items-center gap-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 px-1.5 py-0.5 rounded">👤 담당: {hist.contact_person}</span>
                                    )}

                                    {hist.phone && hist.phone !== "-" && (
                                      <span className="flex items-center gap-0.5 bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">📞 {hist.phone}</span>
                                    )}
                                  </div>
                                  
                                  <div className="whitespace-pre-wrap">{hist.content}</div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-slate-300 opacity-50 py-10">
                        <MessageSquare size={40} className="mb-2" />
                        <p className="text-sm font-black text-center">여기에 파일을 끌어다 놓으시거나<br/>우측 하단 버튼을 눌러 영업 일지를 작성하세요.</p>
                      </div>
                    )}
                  </div>

                  <div className="p-4 bg-slate-50 border-t border-slate-100 shrink-0 flex items-center justify-between gap-4">
                    <div className="text-[11px] font-bold text-slate-400">
                      정형화된 영업 이력(방문 날짜, 예산) 저장을 위해 보고서 양식을 사용하세요.
                    </div>
                    <button 
                      onClick={() => {
                        handleClientSelectInModal(selectedClientName); 
                        setShowReportModal(true);
                      }} 
                      className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black text-xs shadow-lg shadow-blue-100 transition-all active:scale-95 flex items-center gap-2"
                    >
                      <FileText size={14}/> 영업 / CS 보고서 작성하기
                    </button>
                  </div>
               </div>

               {showFileCabinet && (
                  <div className="w-[280px] shrink-0 bg-white rounded-[32px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-right duration-300">
                    <div className="px-5 py-4 bg-blue-600 text-white flex justify-between items-center shrink-0">
                      <div className="flex items-center gap-2">
                        <FolderOpen size={16} />
                        <h3 className="font-black text-[13px]">파일 보관함</h3>
                      </div>
                      <button onClick={() => setShowFileCabinet(false)} className="hover:bg-white/20 rounded-lg p-1"><X size={16}/></button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-slate-50/30 space-y-2">
                      {clientFiles.length > 0 ? (
                        clientFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-2xl border border-slate-100 shadow-sm hover:border-blue-400 transition-all group">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText size={14} className="text-slate-400 shrink-0" />
                              <span className="text-[11px] font-bold text-slate-700 truncate" title={file.name}>{file.name}</span>
                            </div>
                            
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button 
                                onClick={() => window.open(`${API_BASE_URL}/clients/files/download?path=${encodeURIComponent(file.path)}`)} 
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="다운로드"
                              >
                                <Download size={14} />
                              </button>
                              
                              <PermissionGate user={currentUser} action="contacts_edit">
                                <button 
                                  onClick={() => handleDeleteFile(file.path, file.name)} 
                                  className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                                  title="삭제"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </PermissionGate>
                            </div>

                          </div>
                        ))
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center opacity-30 py-10">
                          <FileUp size={32} className="mb-2" />
                          <p className="text-[10px] font-black uppercase text-center"></p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>

            {/* 3. 소속 담당자 명단 */}
            <div className="flex-[0.8] bg-white rounded-[32px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-200/60 flex flex-col overflow-hidden">
               <div className="px-7 py-6 border-b border-slate-100 flex justify-between items-center bg-white shrink-0">
                  <div className="flex items-center gap-3 text-emerald-600">
                    <Users size={20} />
                    <h3 className="font-black text-slate-900 text-[15px]">학과별 담당자</h3>
                  </div>
                  <PermissionGate user={currentUser} action="contacts_edit">
                    <button onClick={() => onNavigate?.('contacts')} className="p-2.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-xl transition-all border border-emerald-100">
                      <Plus size={16} strokeWidth={3} />
                    </button>
                  </PermissionGate>
               </div>
               
               <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6 bg-slate-50/20">
                  {clientDetail?.contacts?.length > 0 ? (
                    Object.entries(
                      clientDetail.contacts.reduce((acc: any, curr: any) => {
                        const dept = curr.department || curr.industry || '미분류 학과/부서';
                        if (!acc[dept]) acc[dept] = [];
                        acc[dept].push(curr);
                        return acc;
                      }, {})
                    ).map(([dept, members]: [string, any]) => (
                      <div key={dept} className="space-y-3">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="h-1.5 w-1.5 bg-emerald-400 rounded-full" />
                          <p className="text-[11px] font-black text-emerald-600 uppercase tracking-tighter">{dept}</p>
                        </div>
                        {members.map((m: any, idx: number) => {
                          const rawName = m.contactPerson || m.contact_person || m.name;
                          const displayName = (!rawName || rawName === '-') ? '이름 미상' : rawName;

                          return (
                            <div key={idx} className="p-4 bg-white border border-slate-100 rounded-2xl shadow-sm group hover:border-emerald-400 hover:translate-x-1 transition-all">
                               <div className="flex justify-between items-start">
                                  <div>
                                     <p className="text-[13px] font-black text-slate-800">{displayName}</p>
                                  </div>
                                  <button className="p-1.5 bg-slate-50 rounded-lg text-slate-300 hover:text-emerald-500 transition-colors">
                                     <MessageSquare size={14} />
                                  </button>
                               </div>
                               <p className="text-[10px] text-slate-500 font-bold mt-3 font-mono">📞 {m.phone || m.email || '-'}</p>
                            </div>
                          );
                        })}
                      </div>
                    ))
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center opacity-20 py-10"><Users size={40} /><p className="text-[11px] font-black mt-3 uppercase tracking-widest">Empty Contacts</p></div>
                  )}
               </div>
            </div>
          </>
        )}
      </div>

      {/* 🚨 [실제 방문일/예산 모달 고도화 연동 기획 반영] 요구사항 매칭형 연동 모달 UI 탑재 */}
      {showReportModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={() => setShowDropdown(false)}>
          <div className="bg-white w-full max-w-lg rounded-[32px] shadow-2xl overflow-hidden animate-fade-in-up flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="px-8 py-5 bg-blue-600 text-white flex justify-between items-center shrink-0">
              <div>
                <h3 className="font-black text-lg">📝 활동 일지 보고서 작성</h3>
                <p className="text-[10px] text-blue-200 font-bold uppercase tracking-wider">Unified Activity & CS Report</p>
              </div>
              <button onClick={() => setShowReportModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors"><X size={20}/></button>
            </div>
            
            <div className="p-8 overflow-y-auto custom-scrollbar space-y-5 flex-1">
              {/* 스위치형 토글 연동 구조 개조 */}
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-[12px] font-black text-slate-500">담당 팀</span>
                <button
                  type="button"
                  onClick={() => setIsCSMode(!isCSMode)}
                  className={`px-5 py-2.5 rounded-xl text-xs font-black border transition-all ${isCSMode ? 'bg-purple-600 text-white border-purple-700 shadow-md shadow-purple-100' : 'bg-blue-600 text-white border-blue-700 shadow-md shadow-blue-100'}`}
                >
                  {isCSMode ? 'CS팀' : '국내팀'}
                </button>
              </div>

              {/* 양방향 연동: 지역 ➔ 거래처 체인 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase flex items-center gap-1">
                    <MapPin size={12} className="text-blue-500"/> 지역 필터 (선택)
                  </label>
                  <select 
                    value={selectedRegion} 
                    onChange={(e) => handleRegionSelectInModal(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-black outline-none focus:bg-white cursor-pointer"
                  >
                    <option value="">전체 권역 보기</option>
                    {availableRegions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                
                {/* 검색 연동 인풋 + 실시간 드롭다운 복합 레이어 설계 */}
                <div className="space-y-1.5 relative">
                  <label className="text-[11px] font-black text-slate-400 uppercase">거래처 검색선택 (필수)</label>
                  <input 
                    type="text" 
                    placeholder="학교명 입력 (예: 전주...)" 
                    value={clientSearchInput}
                    onChange={(e) => { setClientSearchInput(e.target.value); setShowDropdown(true); }}
                    onFocus={() => setShowDropdown(true)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20"
                  />
                  {showDropdown && filteredModalClients.length > 0 && (
                    <div className="absolute left-0 right-0 top-[102%] bg-white border border-slate-200 rounded-2xl shadow-xl max-h-40 overflow-y-auto z-[9999] custom-scrollbar divide-y divide-slate-50">
                      {filteredModalClients.map(c => (
                        <div 
                          key={c.id} 
                          onClick={() => handleClientSelectInModal(c.client_name)}
                          className="px-4 py-2.5 text-[12px] font-bold text-slate-700 hover:bg-blue-50 cursor-pointer flex justify-between"
                        >
                          <span>{c.client_name}</span>
                          <span className="text-[10px] text-slate-400 font-normal">{c.region}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 🚨 [버그 픽스 2] 강제 안전 락 바인딩 - 느슨한 매칭 적용으로 드롭다운 부활 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase">거래처 담당자</label>
                  {(() => {
                    // 🚨 거래처 이름과 담당자를 느슨하게(포함) 찾도록 매칭 로직 변경
                    const matchedContacts = contactsMaster.filter((c: any) => {
                      const company = (c.company || c.company_name || '').trim();
                      return company && (selectedClientName.includes(company) || company.includes(selectedClientName));
                    });

                    return matchedContacts.length > 0 ? (
                      <select 
                        value={selectedContactPerson} 
                        onChange={(e) => handleContactPersonSelectInModal(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-black outline-none focus:bg-white cursor-pointer"
                      >
                        <option value="">-- 담당자 선택 --</option>
                        {matchedContacts.map((m, idx) => (
                          <option key={idx} value={m.contactPerson || m.name}>{m.contactPerson || m.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input 
                        type="text" 
                        readOnly 
                        value="⚠️ 담당자를 등록하세요." 
                        className="w-full px-4 py-3 bg-rose-50 text-rose-600 border border-rose-100 rounded-2xl text-[12px] font-black text-center cursor-not-allowed"
                        title="고객 관리 매트릭스 탭에서 담당자 정보를 사전에 생성해두어야 정식 보고서 제출이 가능합니다."
                      />
                    );
                  })()}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase">연락처 (마스터 락 / 수정불가)</label>
                  <input 
                    type="text" 
                    value={autoPhone ? `📞 ${autoPhone}` : "등록 번호 없음"} 
                    readOnly 
                    className="w-full px-4 py-3 bg-slate-100 text-slate-400 border border-slate-200 rounded-2xl text-[13px] font-mono text-center cursor-not-allowed font-bold"
                  />
                </div>
              </div>

              {/* 실제 방문일/예산 적재 주머니 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase flex items-center gap-1">
                    <Calendar size={12} className="text-blue-500" /> 실제 방문 일자 (필수)
                  </label>
                  <input 
                    type="date" 
                    value={visitDate} 
                    onChange={e => setVisitDate(e.target.value)} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase flex items-center gap-1">
                    <DollarSign size={12} className="text-blue-500" /> 기획 예산액 (선택)
                  </label>
                  <input 
                    type="text" 
                    placeholder="예: 500만원, 미정 등" 
                    value={budget} 
                    onChange={e => setBudget(e.target.value)} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all text-right font-mono"
                  />
                </div>
              </div>

              {/* 기존 내용 기입 텍스트 박스 연동 */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-400 uppercase">상담 세부 내용 (보고서 본문)</label>
                <textarea 
                  value={quickMemo} 
                  onChange={e => setQuickMemo(e.target.value)} 
                  placeholder="미팅 세부조율 안건이나 특이사항 보고 내용을 구체적으로 기록하세요..."
                  className="w-full h-28 px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none resize-none focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all custom-scrollbar"
                />
              </div>

              <button 
                onClick={handleSaveReportModal} 
                disabled={!quickMemo.trim() || !selectedClientName || !selectedContactPerson}
                className={`w-full py-4 text-white font-black text-sm rounded-2xl shadow-xl transition-all active:scale-95 flex items-center justify-center gap-2 mt-2 disabled:bg-slate-300 disabled:cursor-not-allowed ${isCSMode ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-100' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'}`}
              >
                <Send size={16}/> 종합 보고서 서버 저장 및 적재하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 주문 등록 모달 */}
      {showOrderModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-[32px] shadow-2xl overflow-hidden animate-fade-in-up">
            <div className="px-8 py-6 bg-slate-900 text-white flex justify-between items-center">
              <div>
                <h3 className="font-black text-lg">신규 주문서 등록</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Manual Order Entry</p>
              </div>
              <button onClick={() => setShowOrderModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors"><X size={20}/></button>
            </div>
            
            <div className="p-8 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-400 uppercase">거래처명 (필수)</label>
                <input type="text" value={newOrder.client_name} onChange={e => setNewOrder({...newOrder, client_name: e.target.value})} 
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold focus:ring-4 focus:ring-blue-50 outline-none transition-all"/>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase">주문일자</label>
                  <input type="date" value={newOrder.order_date} onChange={e => setNewOrder({...newOrder, order_date: e.target.value})} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase">주문번호</label>
                  <input type="text" placeholder="필수 입력" value={newOrder.order_no} onChange={e => setNewOrder({...newOrder, order_no: e.target.value})} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none"/>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase">고객 담당자</label>
                  <input type="text" placeholder="성함 및 직함" value={newOrder.manager_name} onChange={e => setNewOrder({...newOrder, manager_name: e.target.value})} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase">주문 금액 (₩)</label>
                  <input type="number" placeholder="예: 1500000" value={newOrder.win_price || ''} onChange={e => setNewOrder({...newOrder, win_price: Number(e.target.value)})} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none text-right font-mono focus:ring-4 focus:ring-blue-50 transition-all"/>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-400 uppercase">비고 (특이사항)</label>
                <textarea placeholder="프로젝트명 또는 납품 현장명" value={newOrder.note} onChange={e => setNewOrder({...newOrder, note: e.target.value})} 
                  className="w-full h-24 px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none resize-none"/>
              </div>

              <button onClick={handleSaveOrder} 
                className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95 flex items-center justify-center gap-2 mt-2">
                <ShoppingBag size={18}/> 주문서 저장하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 거래처 정보 수정 모달 */}
      {showEditClientModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-sm rounded-[32px] shadow-2xl overflow-hidden">
            <div className="px-8 py-6 bg-slate-900 text-white flex justify-between items-center">
              <h3 className="font-black text-lg">거래처 정보 수정</h3>
              <button onClick={() => setShowEditClientModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors"><X size={20}/></button>
            </div>
            <div className="p-8 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-400 uppercase">거래처명</label>
                <input type="text" value={editClientData.client_name} onChange={e => setEditClientData({...editClientData, client_name: e.target.value})} 
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none focus:ring-2 focus:ring-blue-500/20"/>
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-400 uppercase">지역 (권역)</label>
                <input type="text" value={editClientData.region} onChange={e => setEditClientData({...editClientData, region: e.target.value})} 
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none focus:ring-2 focus:ring-blue-500/20"/>
              </div>
              <button onClick={handleUpdateClient} className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95 mt-2">
                수정 완료
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 주문서 정보 수정 모달 */}
      {showEditOrderModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-[32px] shadow-2xl overflow-hidden">
            <div className="px-8 py-6 bg-slate-900 text-white flex justify-between items-center">
              <h3 className="font-black text-lg">주문서 정보 수정</h3>
              <button onClick={() => setShowEditOrderModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors"><X size={20}/></button>
            </div>
            <div className="p-8 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase">주문일자</label>
                  <input type="date" value={editOrderData.order_date} onChange={e => setEditOrderData({...editOrderData, order_date: e.target.value})} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none focus:ring-2 focus:ring-blue-500/20"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase">주문번호</label>
                  <input type="text" value={editOrderData.order_no} onChange={e => setEditOrderData({...editOrderData, order_no: e.target.value})} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none focus:ring-2 focus:ring-blue-500/20"/>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase">고객 담당자</label>
                  <input type="text" value={editOrderData.manager_name} onChange={e => setEditOrderData({...editOrderData, manager_name: e.target.value})} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none focus:ring-2 focus:ring-blue-500/20"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase">주문 금액 (₩)</label>
                  <input type="number" value={editOrderData.win_price || ''} onChange={e => setEditOrderData({...editOrderData, win_price: Number(e.target.value)})} 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none focus:ring-2 focus:ring-blue-500/20 font-mono text-right"/>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-400 uppercase">비고 (특이사항)</label>
                <textarea value={editOrderData.note} onChange={e => setEditOrderData({...editOrderData, note: e.target.value})} 
                  className="w-full h-24 px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[13px] font-bold outline-none resize-none focus:ring-2 focus:ring-blue-500/20"/>
              </div>
              <button onClick={handleUpdateOrder} className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95 mt-2">
                수정 완료
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CS 정식 접수 모달 */}
      <CSActivityModal 
        isOpen={showCSModal} 
        onClose={() => setShowCSModal(false)} 
        clientName={selectedClientName} 
        onSuccess={() => handleClientClick(selectedClientId!, selectedClientName)} 
      />

    </div>
  );
}